import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Trash2 } from 'lucide-react';

import { mlabel } from './PerformancePeriodPicker';

/** Write a monthly 1:1 note without opening the whole review sheet. */
export default function PerformanceNoteDialog({ open, trainer, fy, months = [], trainerOptions = [], onClose, onSaved }) {
  const [notes, setNotes] = useState([]);
  const [month, setMonth] = useState('');
  const [note, setNote] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [who, setWho] = useState('');

  const tid = trainer?.trainer_id || who;

  useEffect(() => {
    setWho(trainer?.trainer_id || '');
  }, [trainer]);

  useEffect(() => {
    if (!open || !tid) { setNotes([]); return; }
    setLoading(true);
    api.get(`/training/trainer-performance/trainer/${tid}`, { params: { fy } })
      .then((r) => {
        setNotes(r.data.notes || []);
        const live = (r.data.months || []).map((m) => m.month);
        setMonth(live.slice(-1)[0] || months.slice(-1)[0] || '');
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load the notes'))
      .finally(() => setLoading(false));
  }, [open, tid, fy, months]);

  useEffect(() => {
    const ex = notes.find((n) => n.month === month);
    setNote(ex?.note || '');
    setNext(ex?.next_actions || '');
  }, [month, notes]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/training/trainer-performance/trainer/${tid}/note`,
        { fy, month, note, next_actions: next });
      toast.success(`Note saved for ${mlabel(month)}`);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the note'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/training/trainer-performance/trainer/${tid}/note/${month}`, { params: { fy } });
      toast.success('Note deleted');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
    finally { setBusy(false); }
  };

  const existing = notes.find((n) => n.month === month);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg bg-white" data-testid="perf-note-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Monthly review note</DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            {trainer?.display_name ? `${trainer.display_name} · ` : ''}FY {fy}. One note per month — it shows up on the review sheet
            and on the management copy of the PDF.
          </DialogDescription>
        </DialogHeader>

        {!trainer?.trainer_id && (
          <div className="space-y-1">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Trainer<span className="text-rose-500 ml-0.5">*</span></Label>
            <Select value={who} onValueChange={setWho}>
              <SelectTrigger className="h-9 bg-white text-[13px]" data-testid="note-dialog-trainer"><SelectValue placeholder="Pick a trainer" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {trainerOptions.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}{t.employee_id ? ` · ${t.employee_id}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
        ) : !tid ? (
          <p className="py-6 text-center text-[12.5px] text-slate-400" data-testid="note-dialog-pick-first">Pick a trainer to start.</p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="h-9 w-40 bg-white text-[13px]" data-testid="note-dialog-month"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {months.map((m) => (
                    <SelectItem key={m} value={m}>
                      {mlabel(m)}{notes.some((n) => n.month === m) ? ' · noted' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">What was discussed<span className="text-rose-500 ml-0.5">*</span></Label>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Two cancelled classes, syllabus tracking not started on the Python batch" className="bg-white text-[13px]" data-testid="note-dialog-text" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Agreed next steps</Label>
              <Textarea rows={2} value={next} onChange={(e) => setNext(e.target.value)}
                placeholder="Log topic completion for both running batches by the 10th" className="bg-white text-[13px]" data-testid="note-dialog-next" />
            </div>
            {existing && <p className="text-[11px] text-slate-400">Last saved by {existing.by} on {String(existing.at).slice(0, 10)} — saving replaces it.</p>}
          </div>
        )}

        <DialogFooter>
          {existing && (
            <Button variant="outline" className="text-rose-700" onClick={remove} disabled={busy} data-testid="note-dialog-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
            </Button>
          )}
          <Button variant="ghost" onClick={() => onClose?.()} data-testid="note-dialog-cancel">Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={save}
            disabled={busy || !tid || !month || note.trim().length < 5} data-testid="note-dialog-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
